import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SiteLayout from "../components/SiteLayout";
import { api } from "../lib/api";
import { ArrowUpRight, FileText } from "lucide-react";

const CATS = ["All", "SEO", "Link Building", "Digital PR", "Local SEO", "Content Marketing", "Technical SEO"];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function Blog() {
  const [active, setActive] = useState("All");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/blog", { params: { limit: 100 } });
        setPosts(Array.isArray(data) ? data : []);
      } catch {
        setPosts([]);
      } finally { setLoading(false); }
    })();
  }, []);

  const items = useMemo(
    () => (active === "All" ? posts : posts.filter((p) => p.category === active)),
    [active, posts]
  );

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <SiteLayout>
      <Helmet>
        <title>Blog — SEO, digital PR & link building notes | Zenith Ranking</title>
        <meta name="description" content="Field notes from real campaigns — SEO, digital PR, link building and content strategy from the Zenith Ranking team." />
        <link rel="canonical" href={`${siteUrl}/blog`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Zenith Ranking Journal" />
        <meta property="og:description" content="Field notes from real campaigns — SEO, digital PR, link building and content strategy." />
        <meta property="og:url" content={`${siteUrl}/blog`} />
      </Helmet>

      <section className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 max-w-3xl">
          <div className="eyebrow mb-4">Notes & playbooks</div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight">The Zenith Ranking Journal.</h1>
          <p className="mt-6 text-inkSoft text-lg">Field notes from real campaigns — SEO, digital PR, link building and content strategy.</p>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`tag transition ${active === c ? "bg-ink text-white border-ink" : ""}`}
              data-testid={`blog-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="card-soft overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-surfaceAlt" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-surfaceAlt rounded w-1/3" />
                    <div className="h-6 bg-surfaceAlt rounded w-4/5" />
                    <div className="h-4 bg-surfaceAlt rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="card-soft py-24 text-center">
              <FileText size={28} className="mx-auto text-inkSoft" />
              <div className="font-display text-2xl font-bold tracking-tight mt-4">No posts yet</div>
              <p className="text-inkSoft mt-2 text-sm">Check back soon — new articles land here every week.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((b) => (
                <Link key={b.slug} to={`/blog/${b.slug}`} className="card-soft overflow-hidden group" data-testid={`blog-card-${b.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-surfaceAlt">
                    {b.cover_image_url ? (
                      <img src={b.cover_image_url} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-inkSoft">
                        <FileText size={28} />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="tag tag-accent">{b.category || "SEO"}</span>
                      <span className="text-xs text-inkSoft">{(b.reading_time || 3)} min read</span>
                    </div>
                    <h2 className="font-display text-xl font-semibold tracking-tight group-hover:text-terra transition line-clamp-2">{b.title}</h2>
                    <p className="mt-3 text-sm text-inkSoft line-clamp-3">{b.excerpt}</p>
                    <div className="mt-5 pt-4 border-t border-line flex items-center justify-between text-xs text-inkSoft">
                      <span>{b.author} · {fmtDate(b.published_at)}</span>
                      <ArrowUpRight size={14} className="group-hover:text-terra transition" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
